import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { FormProvider } from "react-hook-form"

import { AddCustomerPurchaseOrderForm } from "./AddCustomerPurchaseOrderForm"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { useCustomerPurchaseOrderForm } from "@/screens/CustomerOrders/hooks/useCustomerPurchaseOrderForm"

interface AddCustomerPurchaseOrderDialogProps {
  companyId: string
  shipToLocationId: string
  onClose: () => void
  onSubmit: () => void
  onDuplicateSelect: (cpoNumber: string) => void
}

export const AddCustomerPurchaseOrderDialog: FC<AddCustomerPurchaseOrderDialogProps> = ({
  companyId,
  shipToLocationId,
  onClose,
  onSubmit,
  onDuplicateSelect,
}) => {
  const {
    methods,
    handleSubmit,
    handleCancel,
    canSubmit,
    isDuplicate,
    handleCreateNew,
    handleViewEditExisting,
    isCreating,
    isCancelConfirmationOpen,
    setIsCancelConfirmationOpen,
  } = useCustomerPurchaseOrderForm({
    companyId,
    shipToLocationId,
    onClose,
    onSubmit,
    onDuplicateSelect,
  })

  return (
    <Dialog
      open
      onClose={handleCancel}
      maxWidth='sm'
      PaperProps={{ sx: { width: 572, borderRadius: 2 } }}
      data-testid='customer-orders.add-cpo-dialog'
    >
      {isCancelConfirmationOpen ? (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogHeader title='Add Customer Purchase Order' onClose={handleCancel} />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <AddCustomerPurchaseOrderForm isDuplicate={isDuplicate} />
            </FormProvider>
          </DialogContent>

          {isDuplicate ? (
            <DialogActions sx={{ px: 5, pb: 5, justifyContent: "space-between" }}>
              <Button
                appearance='outlined'
                onClick={handleCancel}
                data-testid='customer-orders.add-cpo-dialog.cancel-button'
              >
                Cancel
              </Button>
              <Box className='flex gap-2'>
                <Button
                  appearance='text'
                  className='text-primary'
                  onClick={handleCreateNew}
                  loading={isCreating}
                  data-testid='customer-orders.add-cpo-dialog.create-new-button'
                >
                  Create New
                </Button>
                <Button
                  appearance='contained'
                  onClick={handleViewEditExisting}
                  data-testid='customer-orders.add-cpo-dialog.view-edit-existing-button'
                >
                  View / Edit Existing
                </Button>
              </Box>
            </DialogActions>
          ) : (
            <DialogActions sx={{ px: 5, pb: 5, justifyContent: "space-between" }}>
              <Button
                appearance='outlined'
                onClick={handleCancel}
                data-testid='customer-orders.add-cpo-dialog.cancel-button'
              >
                Cancel
              </Button>
              <Button
                appearance='contained'
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={isCreating}
                data-testid='customer-orders.add-cpo-dialog.create-button'
              >
                Create
              </Button>
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  )
}
